/**
 * Deterministic ship kinematics — the heart of prediction == authority.
 *
 * `stepShipBody` is pure given (body, input, wind, dt) and is called with the
 * SAME code on the server (truth) and the client (prediction of the local
 * ship). No wall-clock, no Math.random, in-place mutation for zero GC churn.
 */
import { CARGO_RUN, SHIP, WIND, WORLD_EDGE, WORLD_SIZE } from '@piratas/protocol';
import { approach, clamp, normAngle } from './math.js';
import { tradeWindMul } from './tradewind.js';
import type { ShipBody, ShipInput, Wind } from './types.js';

/**
 * Cargo-Runs weight → top-speed multiplier (0..1). A laden hull is slower; the penalty is NON-LINEAR — cheap
 * at first, brutal near a full hold ("o topo dói"). Piecewise-linear + continuous over the spec bands:
 * 0–50% ⇒ up to −5%, 50–80% ⇒ up to −15%, 80–100% ⇒ up to −40%. Pure + deterministic (no wall-clock/rng), so
 * the server and the client's local prediction fold in the SAME scrub tick-for-tick. Reads the tuning from
 * `CARGO_RUN.WEIGHT_SPEED_CURVE` (protocol — the single source).
 */
export function cargoSpeedMul(load: number): number {
  const c = CARGO_RUN.WEIGHT_SPEED_CURVE;
  const L = clamp(load, 0, 1);
  let pen: number;
  if (L <= c.LIGHT_UNTIL) pen = c.LIGHT_UNTIL > 0 ? c.LIGHT_PENALTY * (L / c.LIGHT_UNTIL) : 0;
  else if (L <= c.MID_UNTIL) pen = c.LIGHT_PENALTY + (c.MID_PENALTY - c.LIGHT_PENALTY) * ((L - c.LIGHT_UNTIL) / (c.MID_UNTIL - c.LIGHT_UNTIL));
  else pen = c.MID_PENALTY + (c.HEAVY_PENALTY - c.MID_PENALTY) * ((L - c.MID_UNTIL) / (1 - c.MID_UNTIL));
  return 1 - pen;
}

/**
 * Point-of-sail factor in [WIND.MIN_FACTOR, 1]. Sailing downwind (heading
 * aligned with where the wind blows toward) is fastest; into the wind ("in
 * irons") is slowest. Arcade, intuitive, Sea-of-Thieves-ish.
 */
export function windFactor(heading: number, windDir: number): number {
  const alignment = Math.cos(heading - windDir); // +1 downwind, -1 upwind
  return WIND.MIN_FACTOR + (1 - WIND.MIN_FACTOR) * (alignment * 0.5 + 0.5);
}

/**
 * Advance a ship body by one fixed timestep. Mutates `b` in place.
 * `speedMul`/`turnMul` apply the player's upgrade levels — the server and the
 * client's local prediction pass the SAME multipliers, so prediction stays
 * byte-for-byte with authority.
 */
export function stepShipBody(b: ShipBody, input: ShipInput, wind: Wind, dt: number, speedMul = 1, turnMul = 1): void {
  // --- sail trim approaches target (anchor forces sails furled) ---
  const sailTarget = input.anchor ? 0 : clamp(input.sailTarget, 0, 1);
  b.sail = approach(b.sail, sailTarget, SHIP.SAIL_TRIM_RATE * dt);

  // --- target forward speed from sail * point-of-sail * wind * trade-wind current ---
  // The trade-wind boost is a PURE function of (x, y, heading) — reading it here (rather than as an
  // externally-passed multiplier) is what keeps prediction == authority: the client's reconcile replay
  // recomputes it from each intermediate position exactly as the server did tick-for-tick.
  const wf = windFactor(b.heading, wind.dir);
  const trade = tradeWindMul(b.x, b.y, b.heading);
  // Cargo Runs: a laden hold scrubs top speed (non-linear). Read straight off the body so the client's
  // reconcile replay recomputes it identically to the server (prediction == authority). Absent ⇒ unladen.
  const laden = cargoSpeedMul(b.cargoLoad ?? 0);
  const targetSpeed = input.anchor ? 0 : SHIP.MAX_SPEED * speedMul * b.sail * wf * wind.strength * trade * laden;

  // stable exponential approach; accelerate slower than we coast/anchor-brake
  const rate = b.speed < targetSpeed
    ? SHIP.ACCEL
    : (input.anchor ? SHIP.ANCHOR_DRAG : SHIP.DRAG);
  b.speed += (targetSpeed - b.speed) * clamp(rate * dt, 0, 1);

  // --- steering: rudder authority grows with speed (no speed, no turn) ---
  const steerAuthority = clamp(b.speed / SHIP.MIN_STEER_SPEED, 0, 1);
  const targetAngVel = input.rudder * SHIP.MAX_TURN_RATE * turnMul * steerAuthority;
  b.angVel = approach(b.angVel, targetAngVel, SHIP.TURN_ACCEL * dt);
  b.heading = normAngle(b.heading + b.angVel * dt);

  // --- integrate position along heading + any transient knockback impulse ---
  b.x += (Math.cos(b.heading) * b.speed + b.knockX) * dt;
  b.y += (Math.sin(b.heading) * b.speed + b.knockY) * dt;
  // decay the shove exponentially (deterministic, identical on server + client prediction)
  if (b.knockX !== 0 || b.knockY !== 0) {
    const kd = Math.exp(-SHIP.KNOCK_DECAY * dt);
    b.knockX *= kd; b.knockY *= kd;
    if (b.knockX * b.knockX + b.knockY * b.knockY < 0.0004) { b.knockX = 0; b.knockY = 0; } // settle (<0.02 m/s)
  }

  // --- world bounds: soft stop at the edge of the arena ---
  if (b.x < 0) { b.x = 0; b.speed *= SHIP.WALL_BOUNCE; }
  else if (b.x > WORLD_SIZE) { b.x = WORLD_SIZE; b.speed *= SHIP.WALL_BOUNCE; }
  if (b.y < 0) { b.y = 0; b.speed *= SHIP.WALL_BOUNCE; }
  else if (b.y > WORLD_SIZE) { b.y = WORLD_SIZE; b.speed *= SHIP.WALL_BOUNCE; }
}

/** A solid circular obstacle (island / base footprint) on the sim plane. */
export interface Obstacle { x: number; y: number; r: number }

/**
 * Push a ship body out of any solid circle it overlaps. Runs AFTER `stepShipBody`
 * on BOTH the server and client prediction with the SAME obstacle list, so the
 * local ship never sails through an island and prediction stays == authority.
 * Pure + deterministic (mutates `b` in place, allocation-free). The ship is
 * approximated by a circle of `shipR`; it slides tangentially along the shore.
 */
export function collideCircles(b: ShipBody, obstacles: readonly Obstacle[], shipR: number): void {
  for (let i = 0; i < obstacles.length; i++) {
    const o = obstacles[i]!;
    const min = o.r + shipR;
    if (min <= 0) continue;
    const dx = b.x - o.x, dy = b.y - o.y;
    const d2 = dx * dx + dy * dy;
    if (d2 >= min * min) continue;              // not overlapping
    const d = Math.sqrt(d2);
    const nx = d > 1e-4 ? dx / d : 1;            // outward normal (degenerate centre -> +x)
    const ny = d > 1e-4 ? dy / d : 0;
    b.x = o.x + nx * min;                        // project to the shore boundary
    b.y = o.y + ny * min;
    // cancel any INWARD ram-knock so a shove can't punch you through the island
    const kin = b.knockX * nx + b.knockY * ny;
    if (kin < 0) { b.knockX -= kin * nx; b.knockY -= kin * ny; }
    // scrub speed on a head-on beaching (heading points into the shore) — you don't bounce, you stall
    if (Math.cos(b.heading) * nx + Math.sin(b.heading) * ny < -0.25) b.speed *= 0.5;
  }
}

/**
 * The map-border "coming about" (Onda Fix 3: a MINIMAL reserved strip). Called AFTER `stepShipBody`
 * (+ `collideCircles`) on BOTH the server and the client's local prediction with the SAME constants, so a
 * ship that reaches the edge turns around identically on both sides (prediction == authority). The band is
 * now thin (`WORLD_EDGE.MARGIN`, sized only to complete the turn before the hard clamp) so the sea is fully
 * navigable right up to the physical limit; a hull that is ANCHORED/berthed/becalmed (speed ≤ `MIN_SPEED`) is
 * left alone. When a moving hull enters the band with the bow pointing outward, it forces a DOMINANT
 * come-about back toward open water:
 *   1. steer the heading toward the inward wall normal (a corner blends both walls to a diagonal),
 *   2. OVERWRITE the ship's angular velocity with that forced turn, so the player's rudder can't fight the
 *      turnaround AND the motion carries smoothly out of the band when it releases (no snap),
 *   3. keep most of the way (a gentle `SPEED_KEEP` scrub) so the hull sweeps a WIDE arc instead of pinning,
 *   4. keep steering until the bow is aimed clearly back inward (`ALIGN`) — not just parallel to the wall —
 *      so the ship leaves the band pointed at open sea, then hands control back to the helm.
 * Pure + deterministic (no wall-clock / rng): the forced `angVel` is a pure function of heading + wall normal
 * + dt, so the client's reconcile replay reconstructs it byte-for-byte from the snapshot. Mutates `b` in
 * place, allocation-free. Returns `true` on the ticks it is actively coming about, so the LOCAL client can
 * flash the edge banner (the server ignores the return). The old hard clamp in `stepShipBody` remains only as
 * an out-of-bounds backstop — the arc completes far inside the margin (worst-case flank penetration
 * ≈ MAX_SPEED / TURN_RATE ≈ 8 m « MARGIN), so a hull never reaches it.
 */
export function enforceWorldEdge(b: ShipBody, dt: number): boolean {
  const S = WORLD_SIZE, m = WORLD_EDGE.MARGIN;
  // inward wall normal, summed over whichever margin bands the hull is inside (a corner blends to a diagonal)
  let nx = 0, ny = 0;
  if (b.x < m) nx += 1; else if (b.x > S - m) nx -= 1;
  if (b.y < m) ny += 1; else if (b.y > S - m) ny -= 1;
  if (nx === 0 && ny === 0) return false;
  const nl = Math.sqrt(nx * nx + ny * ny);
  nx /= nl; ny /= nl;
  // heading·inwardNormal: +1 = bow points straight back into the world, -1 = straight out past the edge
  const into = Math.cos(b.heading) * nx + Math.sin(b.heading) * ny;
  if (into >= WORLD_EDGE.ALIGN) return false;         // bow already aimed clearly inward → release the helm
  if (b.speed <= WORLD_EDGE.MIN_SPEED) return false;  // anchored/berthed/becalmed at the shore → don't shake a
                                                      // stationary hull (it can't cross the hard clamp anyway)
  const target = Math.atan2(ny, nx);                  // steer the bow at the inward normal
  let d = normAngle(target - b.heading);
  const max = WORLD_EDGE.TURN_RATE * dt;
  if (d > max) d = max; else if (d < -max) d = -max;
  b.heading = normAngle(b.heading + d);
  // DOMINATE the helm: overwrite angular velocity with the forced turn. The player's rudder cannot hold
  // against the come-about, and when the band releases this inward angVel eases the exit (stepShipBody's
  // approach() blends it back toward the rudder target) instead of snapping. Deterministic → no desync.
  b.angVel = d / dt;
  b.speed *= Math.max(0, 1 - (1 - WORLD_EDGE.SPEED_KEEP) * dt); // gentle per-second scrub — keep the way, no pinning
  return true;
}

/** Default hull half-extents (the brigantine/base hull). Per-class hulls pass
 * their own half-length/half-beam so a galleon is a genuinely bigger OBB target. */
const HALF_L = SHIP.LENGTH / 2;
const HALF_B = SHIP.BEAM / 2;

/**
 * World-space hull control points (forward = +x local): [bow, stern, port,
 * starboard]. Used for buoyancy sampling and OBB-vs-OBB tests. Reuses `out` for
 * zero GC. `halfL`/`halfB` default to the base hull; pass class dims for a
 * per-class hull box.
 */
export function shipControlPoints(
  b: ShipBody, out: Array<{ x: number; y: number }> = [], halfL = HALF_L, halfB = HALF_B,
): Array<{ x: number; y: number }> {
  const cos = Math.cos(b.heading);
  const sin = Math.sin(b.heading);
  for (let i = 0; i < 4; i++) if (!out[i]) out[i] = { x: 0, y: 0 };
  // bow(+x), stern(-x), port(-y), starboard(+y)
  out[0]!.x = b.x + halfL * cos; out[0]!.y = b.y + halfL * sin;
  out[1]!.x = b.x - halfL * cos; out[1]!.y = b.y - halfL * sin;
  out[2]!.x = b.x + halfB * sin; out[2]!.y = b.y - halfB * cos;
  out[3]!.x = b.x - halfB * sin; out[3]!.y = b.y + halfB * cos;
  out.length = 4;
  return out;
}

/**
 * Transform a world point into a body's local hull frame. An |lx| <= LENGTH/2
 * and |ly| <= BEAM/2 result means the point is inside the hull OBB — used for
 * cannonball hit resolution and the local impact point of a leak.
 */
export function worldToHullLocal(b: ShipBody, wx: number, wy: number): { lx: number; ly: number } {
  const cos = Math.cos(b.heading);
  const sin = Math.sin(b.heading);
  const dx = wx - b.x;
  const dy = wy - b.y;
  return {
    lx: dx * cos + dy * sin,
    ly: -dx * sin + dy * cos,
  };
}

/** Is a world point inside this ship's hull OBB (with optional padding + class dims)? */
export function pointInHull(b: ShipBody, wx: number, wy: number, pad = 0, halfL = HALF_L, halfB = HALF_B): boolean {
  const { lx, ly } = worldToHullLocal(b, wx, wy);
  return Math.abs(lx) <= halfL + pad && Math.abs(ly) <= halfB + pad;
}

// Scratch arrays for hullsOverlap (single-threaded sim → safe to reuse, zero GC).
const _ovA: Array<{ x: number; y: number }> = [];
const _ovB: Array<{ x: number; y: number }> = [];

/**
 * Do two hull OBBs overlap? Tests each ship's 4 control points against the
 * other's hull box (with per-class half-extents). Cheap and deterministic — no
 * full SAT — which is plenty for chunky naval hulls that overlap for several
 * ticks at sailing speeds. Used by the server's ramming pass.
 */
export function hullsOverlap(
  a: ShipBody, aHalfL: number, aHalfB: number,
  b: ShipBody, bHalfL: number, bHalfB: number,
): boolean {
  const ca = shipControlPoints(a, _ovA, aHalfL, aHalfB);
  for (let i = 0; i < ca.length; i++) if (pointInHull(b, ca[i]!.x, ca[i]!.y, 0, bHalfL, bHalfB)) return true;
  const cb = shipControlPoints(b, _ovB, bHalfL, bHalfB);
  for (let i = 0; i < cb.length; i++) if (pointInHull(a, cb[i]!.x, cb[i]!.y, 0, aHalfL, aHalfB)) return true;
  return false;
}
